import { produce } from 'immer';
import { pull } from 'lodash-es';
import type { StateSnapshot } from 'react-virtuoso';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { ConversationFormDataType } from '@/components/conversations/conversation-form';
import { allChatSessionQueryOptions } from '@/hooks/sessions/use-conversation-sessions';
import type { ConversationMessageTypeUnion } from '@/models/conversations/conversation';
import type {
  ConversationGroupType,
  ConversationSessionsTypeUnion,
  ConversationSessionType,
} from '@/models/conversations/conversation-session';
import ROUTES from '@/routes';
import { createStorageByUserId } from '@/services/storage-manager';
import { useMessageStore } from '@/stores/message';
import { getAllSessions, getSessionByKey, getSessions, updateSessionUnreadCount } from '@/utils/cache-session';
import { SESSIONS_STORAGE_KEY } from '@/utils/constants';
import { queryClient } from '@/utils/query-client';
import { generateSessionKey, parseSessionKey, type SessionKey } from '@/utils/session';

type SessionType = SessionKey extends `session-${infer _}` ? ConversationSessionType : ConversationGroupType;

export type DraftType = Pick<ConversationFormDataType, 'Content' | 'AtIDs' | 'Quote'> & { Timestamp: number };

// export type PositionSnapshotType = number;
export type PositionSnapshotType = (StateSnapshot & { anchorId?: number }) | number;

interface ConversationSessionState {
  hydrated: boolean;
  lastReadFriendRequestId: number;
  lastReadNotices: Record<string, string>;
  lastPositions: Record<string, Record<number | string, PositionSnapshotType | undefined>>;
  hiddenSessions: string[];
  subscribedSessions: { userId: number }[];
  drafts: Record<string, DraftType>;
}

interface ConversationSessionActions {
  addSession(session: ConversationSessionsTypeUnion): void;

  removeSession(sessionKey: SessionKey): ConversationSessionsTypeUnion | undefined;

  removeSessionByUserId(userId: number): void;

  subscribeSessionByUserId(userId: number): void;

  unSubscribeSessionByUserId(userId: number): void;

  setLastReadNotice(sessionKey: string, notice: string | null | undefined): void;

  addUnread(sessionKey: string, message?: ConversationMessageTypeUnion, updateCount?: number): void;

  clearUnreadCount(sessionKey: string, messageId?: number): void;

  updateSession(sessionKey: SessionKey, session: Partial<Omit<SessionType, 'ID' | 'UserID' | 'SessionType'>>): void;

  hideSession(sessionKey: string): void;

  unhideSession(sessionKey: string): void;

  hideAllCurrentSessions(): void;

  clear(): void;

  setHydrated(hasHydrated: boolean): void;

  updateDraft(sessionKey: string, draft: Omit<DraftType, 'Timestamp'>): void;

  deleteDraft(sessionKey: string): void;

  clearDrafts(): void;

  setLastPosition(sessionKey: string, position: PositionSnapshotType, messageId?: number | 'default'): void;

  clearLastPosition(sessionKey: string): void;

  clearLastPositions(options?: { keepMainListPosition: boolean }): void;

  setLastReadFriendRequestId(id: number): void;
}

type ConversationSessionStore = ConversationSessionState & ConversationSessionActions;

export const useConversationSessionStore = create<ConversationSessionStore>()(
  persist(
    immer(
      (set, get): ConversationSessionStore => ({
        hydrated: false as boolean,
        lastReadFriendRequestId: 0,
        lastReadNotices: {} as Record<string, string>,
        lastPositions: {} as Record<string, Record<number | 'default', PositionSnapshotType>>,
        hiddenSessions: [] as string[],
        subscribedSessions: [] as { userId: number }[],
        drafts: {} as Record<string, DraftType>,

        setLastReadNotice(sessionKey, notice) {
          set((state) => {
            if (notice === null) {
              delete state.lastReadNotices[sessionKey];
            } else if (notice !== undefined) {
              state.lastReadNotices[sessionKey] = notice;
            }
          });
        },

        addUnread(sessionKey, message, updateCount = 1) {
          const [type, id] = parseSessionKey(sessionKey);
          if (!id) return;

          const currentMessagesKey = useMessageStore.getState().key;
          if (currentMessagesKey !== sessionKey)
            updateSessionUnreadCount(type, id, ({ UnReadCount: prevCount }) =>
              prevCount === Number.POSITIVE_INFINITY ? updateCount : prevCount + updateCount,
            );

          if (!message || message.Status === null) return;

          queryClient.setQueryData(allChatSessionQueryOptions.queryKey, (state) =>
            produce(state, (draft) => {
              if (draft) {
                const session = draft.find((s) => generateSessionKey(s.SessionType, s.ID) === sessionKey);

                if (session) {
                  session.LastMsgID = message.ID;
                  session.LastMsgType = message.MsgType;
                  session.LastMsg = message.Message;
                  session.LastMsgTimeStamp = message.CreateTime;
                  if (session.SessionType === 'group' && 'GroupID' in message) {
                    // 群組最新消息的傳送者UserID和UserNickName
                    session.UserID = message.UserID;
                    session.UserNickName = message.UserNickName;
                    session.GotAt = message.GotAt ?? false;
                  }
                }
              }
              return draft;
            }),
          );

          get().unhideSession(sessionKey);
        },
        clearUnreadCount(sessionKey) {
          const [type, id] = parseSessionKey(sessionKey);
          if (!id) return;
          updateSessionUnreadCount(type, id, 0);
        },
        addSession(session) {
          queryClient.setQueryData(allChatSessionQueryOptions.queryKey, (state) => {
            if (!state) return [session];

            return state.concat([session]);
          });
        },
        removeSession(sessionKey) {
          queryClient.setQueryData(allChatSessionQueryOptions.queryKey, (state) =>
            produce(state, (draft) => {
              if (!draft) return;
              const sessionIndex = draft.findIndex((s) => generateSessionKey(s.SessionType, s.ID) === sessionKey);
              if (sessionIndex >= 0) {
                draft.splice(sessionIndex, 1);
              }
            }),
          );

          set((state) => {
            state.hiddenSessions = pull(state.hiddenSessions, sessionKey);
            delete state.lastReadNotices[sessionKey];
            delete state.drafts[sessionKey];
          });

          return getSessionByKey(sessionKey);
        },
        removeSessionByUserId(userId) {
          const session = getSessions('session').find((s) => s.UserID === userId);

          if (session) {
            get().removeSession(generateSessionKey('session', session.ID));
          }
        },
        subscribeSessionByUserId(userId) {
          const isAlreadySubscribed = get().subscribedSessions.some(({ userId: id }) => id === userId);
          if (isAlreadySubscribed) return;

          set((state) => {
            state.subscribedSessions.push({
              userId,
            });
          });
        },
        unSubscribeSessionByUserId(userId) {
          set((state) => {
            state.subscribedSessions = state.subscribedSessions.filter((session) => session.userId !== userId);
          });
        },
        clear() {
          set((state) => {
            queryClient.removeQueries({
              queryKey: allChatSessionQueryOptions.queryKey,
            });
            state.hiddenSessions = [];
            state.lastReadNotices = {};
            state.lastPositions = {};
            state.drafts = {};
          });
        },
        setHydrated(hasHydrated: boolean) {
          set((state) => {
            state.hydrated = hasHydrated;
          });
        },
        updateSession(sessionKey, partialSession) {
          queryClient.setQueryData(allChatSessionQueryOptions.queryKey, (state) =>
            produce(state, (draft) => {
              if (!draft) return;

              const session = draft.find((s) => generateSessionKey(s.SessionType, s.ID) === sessionKey);
              if (session) Object.assign(session, partialSession);
            }),
          );
        },
        hideSession(sessionKey: string) {
          set((state) => {
            queryClient.setQueryData(allChatSessionQueryOptions.queryKey, (state) =>
              produce(state, (draft) => {
                if (!draft) return;

                const session = draft.find((s) => generateSessionKey(s.SessionType, s.ID) === sessionKey);
                if (session) {
                  session.UnReadCount = 0;
                  session.UnReadMaxID = session.LastMsgID;
                }
              }),
            );
            state.hiddenSessions.push(sessionKey);
          });
        },
        hideAllCurrentSessions() {
          set((state) => {
            state.hiddenSessions = getAllSessions().map((s) => generateSessionKey(s.SessionType, s.ID));
          });
        },
        unhideSession(sessionKey: string) {
          set((state) => {
            state.hiddenSessions = state.hiddenSessions.filter((s) => s !== sessionKey);
          });
        },
        updateDraft(sessionKey, draft) {
          set((state) => {
            state.drafts[sessionKey] = {
              ...draft,
              Content: draft.Content?.trim(),
              Timestamp: Date.now(),
            };
          });
        },
        deleteDraft(sessionKey) {
          set((state) => {
            delete state.drafts[sessionKey];
          });
        },
        clearDrafts() {
          set((state) => {
            state.drafts = {};
          });
        },

        setLastPosition(sessionKey, position, messageId = 'default') {
          set((state) => {
            state.lastPositions = {
              ...state.lastPositions,
              [sessionKey]: {
                ...state.lastPositions[sessionKey],
                [messageId]: position,
              },
            };
          });
        },
        clearLastPosition(sessionKey) {
          set((state) => {
            delete state.lastPositions[sessionKey];
          });
        },
        clearLastPositions(options) {
          set((state) => {
            const mainListPosition = {
              [ROUTES.MESSAGES.$path()]: {
                default: state.lastPositions[ROUTES.MESSAGES.$path()]?.['default'],
              },
            };
            state.lastPositions = options?.keepMainListPosition ? mainListPosition : {};
          });
        },
        setLastReadFriendRequestId(id) {
          set((draft) => {
            if (draft.lastReadFriendRequestId < id) {
              draft.lastReadFriendRequestId = id;
            }
          });
        },
      }),
    ),
    {
      name: SESSIONS_STORAGE_KEY,
      version: 9,
      storage: createStorageByUserId('INDEXEDDB'),
      partialize: (state) => ({
        lastReadNotices: state.lastReadNotices,
        lastPositions: state.lastPositions,
        hiddenSessions: state.hiddenSessions,
        drafts: state.drafts,
        lastReadFriendRequestId: state.lastReadFriendRequestId,
      }),
      onRehydrateStorage: () => (state, error) => {
        if (error) {
          console.error(error);
        }
        state?.setHydrated(true);
      },
      migrate: () => ({
        lastReadNotices: {},
        lastPositions: {},
        hiddenSessions: [] as string[],
        drafts: {},
        lastReadFriendRequestId: 0,
      }),
    },
  ),
);

export function promisifySessionsHydration() {
  return new Promise<void>((resolve) => {
    if (useConversationSessionStore.getState().hydrated) {
      resolve();
    } else {
      const unsubscribe = useConversationSessionStore.subscribe(({ hydrated }) => {
        if (hydrated) {
          unsubscribe();
          resolve();
        }
      });
    }
  });
}

export const clearLastPosition = (key: string | undefined, options?: { keepMainListPosition: boolean }) => {
  if (key) {
    useConversationSessionStore.getState().clearLastPosition(key);
  } else {
    useConversationSessionStore.getState().clearLastPositions(options);
  }
};
